import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  EmailProfile,
  insightsHeader,
  Octopus,
} from '@maximizer/core/shared/domain';
import { map, Observable, of } from 'rxjs';
import { ContextService } from './context.service';

@Injectable()
export class EmailProfileService {
  private static readonly BulkPrefix = `${Octopus.EmailProfileCode}.`;

  constructor(
    private readonly http: HttpClient,
    private readonly context: ContextService,
  ) {}
  getEmailProfile(emailAddress: string): Observable<EmailProfile[]> {
    return this.getEmailProfiles([emailAddress]);
  }

  // One read covering all the given addresses. $IN is not implemented for EmailProfile,
  // so several addresses are matched with $OR.
  getEmailProfiles(emailAddresses: string[]): Observable<EmailProfile[]> {
    const addresses = [...new Set(emailAddresses.filter(Boolean))];
    if (addresses.length === 0) {
      return of([]);
    }

    const readRequest: Octopus.EmailProfileReadRequest = {
      EmailProfile: {
        Criteria: {
          SearchQuery:
            addresses.length === 1
              ? { EmailAddress: { $EQ: addresses[0] } }
              : {
                  $OR: addresses.map((address) => ({
                    EmailAddress: { $EQ: address },
                  })),
                },
        },
        Scope: {
          Fields: {
            Key: 1,
            EmailAddress: 1,
            EmailType: {
              Key: 1,
              DisplayValue: 1,
            },
            Comment: {
              Key: 1,
              DisplayValue: 1,
            },
            EnrollDate: 1,
            ExpiryDate: 1,
          },
        },
      },
    };
    return this.http
      .post<Octopus.EmailProfileResponse>(
        `${this.context.api}${Octopus.Action.READ}`,
        readRequest,
      )
      .pipe(
        map((result) => {
          if (result.Code === Octopus.ResponseStatusCode.Successful) {
            const mapper = new Octopus.EmailProfileMapper();
            return mapper.from(result.EmailProfile.Data);
          }
          return [];
        }),
      );
  }
  create(emailProfiles?: EmailProfile[]): Observable<(string | null)[] | null> {
    const request: { [name: string]: unknown } = {};

    if (emailProfiles) {
      emailProfiles.forEach(
        (emailProfile, index) =>
          (request['EmailProfile.' + index] = { Data: emailProfile }),
      );
    }

    return this.http
      .post<Octopus.EmailProfileBulkWriteResponse>(
        `${this.context.api}${Octopus.Action.CREATE}`,
        request,
        { headers: this.getRequestHeaders() },
      )
      .pipe(
        map((result) => {
          if (result.Code !== Octopus.ResponseStatusCode.Successful)
            return null;

          const keys: (string | null)[] = (emailProfiles ?? []).map(() => null);
          Object.keys(result)
            .filter((name) => name.startsWith(EmailProfileService.BulkPrefix))
            .forEach((name) => {
              const index = Number(
                name.slice(EmailProfileService.BulkPrefix.length),
              );
              if (!Number.isInteger(index) || index < 0 || index >= keys.length)
                return;
              keys[index] =
                result[name as `EmailProfile.${number}`]?.Data?.Key ?? null;
            });
          return keys;
        }),
      );
  }

  update(emailProfiles?: EmailProfile[]): Observable<boolean> {
    if (!emailProfiles || emailProfiles.length === 0) {
      return of(true);
    }
    const request: { [name: string]: unknown } = {};

    if (emailProfiles) {
      emailProfiles.forEach(
        (emailProfile, index) =>
          (request['EmailProfile.' + index] = { Data: emailProfile }),
      );
    }

    return this.http
      .post<Octopus.ActivityResponse>(
        `${this.context.api}${Octopus.Action.UPDATE}`,
        request,
        { headers: this.getRequestHeaders() },
      )
      .pipe(
        map((result) => {
          return result.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }
  delete(emailProfileKeys: string[]): Observable<boolean> {
    if (!emailProfileKeys || emailProfileKeys.length === 0) {
      return of(true);
    }
    const request: { [name: string]: unknown } = {};

    if (emailProfileKeys) {
      emailProfileKeys.forEach(
        (key, index) =>
          (request['EmailProfile.' + index] = { Data: { Key: key } }),
      );
    }

    return this.http
      .post<Octopus.Response>(
        `${this.context.api}${Octopus.Action.DELETE}`,
        request,
        { headers: this.getRequestHeaders() },
      )
      .pipe(
        map((result) => {
          return result.Code === Octopus.ResponseStatusCode.Successful;
        }),
      );
  }
  private getRequestHeaders(): { [header: string]: string } {
    return {
      [insightsHeader('entity')]: 'EmailProfile',
    };
  }
}
